'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
}

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQ[] = [
    {
      question: "Is Signaly really free to download?",
      answer: "Yes! Signaly is completely free to download from both the App Store and Google Play. You can start with our free plan which includes limited signals per day. Upgrade to premium anytime for unlimited signals and advanced features."
    },
    {
      question: "What devices are compatible with Signaly?",
      answer: "Signaly works on iPhone (iOS 14.0+), iPad (iPadOS 14.0+), and Android devices (Android 8.0+). We also support modern web browsers including Chrome 90+, Safari 14+, Firefox 88+, and Edge 90+."
    },
    {
      question: "Do I need trading experience to use Signaly?",
      answer: "No! Signaly is designed for traders of all levels. Our AI does the complex analysis for you, providing clear buy/sell signals with confidence ratings. We also offer paper trading so you can practice risk-free before trading with real money."
    },
    {
      question: "How do I receive trading signals?",
      answer: "Once you download the app and create an account, you'll receive real-time push notifications whenever our AI identifies a high-confidence trading opportunity. You can also check the app anytime to see all active signals."
    },
    {
      question: "Can I use Signaly offline?",
      answer: "The mobile app allows you to view your trading history and saved signals offline. However, you'll need an internet connection to receive new real-time signals and execute trades."
    },
    {
      question: "Is my financial data secure?",
      answer: "Absolutely. We use bank-level 256-bit SSL encryption, are SOC 2 compliant, and never store your exchange credentials. Signaly only provides signals - you maintain full control of your funds through your own exchange accounts."
    },
    {
      question: "What's the difference between the free and premium plans?",
      answer: "The free plan includes 3 signals per day and basic features. Premium plans offer unlimited signals, advanced analytics, priority support, paper trading, and exclusive community access. Check our pricing page for detailed comparisons."
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Yes! You can cancel your premium subscription at any time from the app settings. You'll continue to have access until the end of your billing period, then automatically revert to the free plan."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 px-4 lg:px-8 bg-card">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-muted-foreground">
            Everything you need to know about downloading and using Signaly
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-background border border-border rounded-xl overflow-hidden hover:shadow-cyan-glow transition-all duration-200">
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left"
              >
                <span className="text-lg font-heading font-semibold text-foreground pr-4">{faq.question}</span>
                <Icon
                  name="ChevronDownIcon"
                  size={24}
                  className={`text-primary flex-shrink-0 transition-transform duration-200 ${openIndex === index ? 'rotate-180' : ''}`}
                />
              </button>
              {openIndex === index && (
                <div className="px-6 pb-6">
                  <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">Still have questions?</p>
          <a
            href="/contact"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-heading font-semibold hover:scale-105 transition-all duration-200 shadow-cyan-glow"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={20} />
            <span>Contact Support</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;